import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, Radio, Square } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Browser-based live broadcaster:
 *   getUserMedia -> MediaRecorder (webm vp8/opus) -> WebSocket -> backend ffmpeg -> Mux RTMPS
 *
 * Props:
 *  - broadcast: { id, live_playback_id, title, section } from POST /api/live/start
 *  - onEnded(): called when broadcast stops (so parent can open the finish modal)
 */
export const LiveBroadcaster = ({ broadcast, onEnded }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const wsRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [camOn, setCamOn] = useState(true);
  const [status, setStatus] = useState("connecting"); // 'connecting' | 'live' | 'ended' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [debug, setDebug] = useState("");

  useEffect(() => {
    let active = true;
    let timer = null;

    (async () => {
      try {
        // 1. Request webcam + mic
        const ms = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (!active) {
          ms.getTracks().forEach((t) => t.stop());
          return;
        }

        // Sanity check: did we actually get a video track?
        const videoTracks = ms.getVideoTracks();
        if (videoTracks.length === 0 || !videoTracks[0].enabled || videoTracks[0].readyState !== "live") {
          ms.getTracks().forEach((t) => t.stop());
          setStatus("error");
          const msg = "No camera detected. Another app may be using it (close the Windows Camera app, Zoom, Teams), or check Windows Settings → Privacy & Security → Camera and make sure Google Chrome is allowed.";
          setErrorMsg(msg);
          toast.error("Camera not detected.");
          return;
        }

        streamRef.current = ms;
        if (videoRef.current) videoRef.current.srcObject = ms;

        // 2. Connect WebSocket
        const token = localStorage.getItem("lmb_token");
        const httpUrl = process.env.REACT_APP_BACKEND_URL;
        const wsUrl = httpUrl.replace(/^http/, "ws");
        const url = `${wsUrl}/api/live/broadcast/${broadcast.id}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        ws.binaryType = "arraybuffer";
        wsRef.current = ws;

        ws.onopen = () => {
          if (!active) return;

          // Ensure audio track is enabled (in case a previous attempt left it disabled)
          ms.getAudioTracks().forEach((t) => { t.enabled = true; });

          // Pick the most reliable mimeType that explicitly includes audio (opus)
          const candidates = [
            "video/webm;codecs=vp8,opus",
            "video/webm;codecs=vp9,opus",
            "video/webm;codecs=h264,opus",
            "video/webm;codecs=opus",
            "video/webm",
          ];
          const mime = candidates.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
          const aTracks = ms.getAudioTracks();
          const vTracks = ms.getVideoTracks();
          const dbg = `codec: ${mime} · audio tracks: ${aTracks.length}${aTracks[0] ? ` (enabled=${aTracks[0].enabled}, muted=${aTracks[0].muted}, label=${aTracks[0].label})` : ""} · video tracks: ${vTracks.length}`;
          setDebug(dbg);
          // eslint-disable-next-line no-console
          console.log("[LiveBroadcaster]", dbg);

          const rec = new MediaRecorder(ms, {
            mimeType: mime,
            videoBitsPerSecond: 2_500_000,
            audioBitsPerSecond: 128_000,
          });
          recorderRef.current = rec;
          rec.ondataavailable = (e) => {
            if (e.data && e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
              e.data.arrayBuffer().then((buf) => {
                if (ws.readyState === WebSocket.OPEN) ws.send(buf);
              });
            }
          };
          rec.start(500); // emit every 0.5s
          setStatus("live");
          // elapsed timer
          const startedAt = Date.now();
          timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
        };

        ws.onerror = () => {
          if (!active) return;
          setStatus("error");
          setErrorMsg("Connection error. Please end and try again.");
        };

        ws.onclose = (e) => {
          if (!active) return;
          if (status !== "ended") {
            // unexpected close
            setStatus((s) => (s === "live" ? "ended" : s));
          }
          if (e.code >= 4000) setErrorMsg(e.reason || "Broadcast connection closed");
        };
      } catch (err) {
        if (!active) return;
        setStatus("error");
        const msg = err?.name === "NotAllowedError"
          ? "Camera/microphone access was denied. Please allow it in your browser settings and try again."
          : err?.message || "Could not start broadcast.";
        setErrorMsg(msg);
        toast.error(msg);
      }
    })();

    return () => {
      active = false;
      if (timer) clearInterval(timer);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcast.id]);

  const cleanup = () => {
    try { recorderRef.current?.stop(); } catch (e) { void e; }
    try { wsRef.current?.close(); } catch (e) { void e; }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { void e; }
  };

  const toggleMute = () => {
    const tracks = streamRef.current?.getAudioTracks() || [];
    tracks.forEach((t) => (t.enabled = muted));
    setMuted(!muted);
  };
  const toggleCam = () => {
    const tracks = streamRef.current?.getVideoTracks() || [];
    tracks.forEach((t) => (t.enabled = !camOn));
    setCamOn(!camOn);
  };

  const endBroadcast = () => {
    cleanup();
    setStatus("ended");
    onEnded?.();
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="live-broadcaster"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <span
          data-testid="live-status-pill"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px",
            background: status === "live" ? "var(--brand-accent)" : "var(--bg-alt)",
            color: status === "live" ? "#FAF9F5" : "var(--text-muted)",
            fontFamily: "'Outfit', sans-serif",
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {status === "live" && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#FAF9F5",
                animation: "pulse 1.4s ease-in-out infinite",
              }}
            />
          )}
          {status === "connecting" && "Connecting…"}
          {status === "live" && `Live · ${fmt(elapsed)}`}
          {status === "ended" && "Ended"}
          {status === "error" && "Error"}
        </span>
        <div style={{ flex: 1 }} />
        <span className="overline" style={{ color: "var(--text-muted)" }}>
          {broadcast.title || "Untitled"} {broadcast.section ? `· ${broadcast.section}` : ""}
        </span>
      </div>

      <div
        style={{
          position: "relative",
          background: "#000",
          aspectRatio: "16 / 9",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {!camOn && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "#1a1a1a",
              display: "grid",
              placeItems: "center",
              color: "#FAF9F5",
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontSize: 18,
            }}
          >
            Camera off
          </div>
        )}
      </div>

      {errorMsg && (
        <p
          style={{
            marginTop: 14,
            color: "var(--brand-accent)",
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: 15,
          }}
        >
          {errorMsg}
        </p>
      )}

      {debug && (
        <p
          data-testid="broadcaster-debug"
          style={{
            marginTop: 10,
            color: "var(--text-muted)",
            fontFamily: "monospace",
            fontSize: 11,
            wordBreak: "break-word",
          }}
        >
          {debug}
        </p>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="live-toggle-mic"
          onClick={toggleMute}
          className="btn-secondary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {muted ? <MicOff size={14} /> : <Mic size={14} />}
          {muted ? "Unmute" : "Mute"}
        </button>
        <button
          type="button"
          data-testid="live-toggle-cam"
          onClick={toggleCam}
          className="btn-secondary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {camOn ? <Video size={14} /> : <VideoOff size={14} />}
          {camOn ? "Camera off" : "Camera on"}
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="live-end-button"
          onClick={endBroadcast}
          className="btn-primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "var(--brand-accent)" }}
        >
          <Square size={14} fill="#FAF9F5" /> End broadcast
        </button>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }`}</style>
    </div>
  );
};

export default LiveBroadcaster;
